import { X, ChevronLeft, ChevronRight } from "lucide-react";

interface SpecialEventMedia {
  id: string;
  event_id: string;
  title: string;
  description: string | null;
  image_url: string;
  video_url: string | null;
  media_type: string;
  display_order: number;
}

interface SpecialEventLightboxProps {
  media: SpecialEventMedia;
  currentIndex: number;
  totalCount: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

const getYouTubeVideoId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
};

const getYouTubeEmbedUrl = (url: string): string => {
  const videoId = getYouTubeVideoId(url);
  return `https://www.youtube.com/embed/${videoId}`;
};

export const SpecialEventLightbox = ({
  media,
  currentIndex,
  totalCount,
  onClose,
  onPrevious,
  onNext
}: SpecialEventLightboxProps) => {
  const isYouTube = media.video_url && (media.video_url.includes('youtube.com') || media.video_url.includes('youtu.be'));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Navigation Buttons - Hidden on mobile */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-50"
        >
          <ChevronLeft className="w-12 h-12" />
        </button>
      )}
      {currentIndex < totalCount - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-50"
        >
          <ChevronRight className="w-12 h-12" />
        </button>
      )}

      <div
        className="max-w-7xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {media.media_type === "video" && media.video_url ? (
          isYouTube ? (
            <iframe
              src={getYouTubeEmbedUrl(media.video_url)}
              className="w-full max-h-[70vh] aspect-video rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={media.video_url}
              controls
              autoPlay
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
          )
        ) : (
          <img
            src={media.image_url}
            alt={media.title}
            className="w-full max-h-[70vh] object-contain rounded-lg"
          />
        )}
        <div className="mt-4 text-white text-center">
          <h3 className="text-2xl font-semibold mb-2">{media.title}</h3>
          {media.description && (
            <p className="text-gray-300">{media.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};
